import { Fragment, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import Head from 'next/head';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';

// Utils
import renderBlock from '../../../src/utils/blocks';
import { URL_SEARCH_PAGES } from '../../../src/constants';

// Hooks
import useBookingUrl from '../../../src/hooks/useBookingUrl';

// Components
import PageTransition from '../PageTransition/PageTransition';
import PageLayout from '../PageLayout/PageLayout';
import ResidenceMenuNavigation from '../../organisms/ResidenceMenuNavigation/ResidenceMenuNavigation';
import BreadCrumb from '../../atoms/BreadCrumb/BreadCrumb';

function PageResidence({
  page, slugs,
}) {
  // Hooks
  const router = useRouter();
  const { t } = useTranslation();
  // States
  const data = useMemo(() => page?.[0] || {}, [page?.[0]]);
  const residence = useMemo(() => data?.data?.residence || {}, [data?.data?.residence]);
  const breadcrumbItems = [
    {
      name: t('breadcrumb.home'),
      position: 1,
      item: '/',
    },
    {
      name: t(`common.activities.${data?.data?.activity}`),
      position: 2,
      item: `/${URL_SEARCH_PAGES[router.locale]}`,
    },
    {
      name: residence?.data?.nameShort,
      position: 3,
      item: null,
    },
  ];
  const { bookingUrl } = useBookingUrl({
    activity: data?.data?.activity,
    shortStayExternalUrl: residence?.data?.shortStayExternalUrl,
    contentIdResidence: residence?.contentId,
  });

  useEffect(() => {
    const handleRouteChange = (url, { shallow }) => {
      if (url === '/contact' || url === `/${router.locale}/contact`) {
        router.push(`/${url}#residence=${residence?.data?.pmsId}&number=${data?.data?.phone.replace(/ /g, '')}`, undefined, { shallow });
      }
    };
    router.events.on('routeChangeStart', handleRouteChange);

    return () => {
      router.events.off('routeChangeStart', handleRouteChange);
    };
  }, [router]);

  return (
    <>
      <Head>
        <title>{data?.data?.metaTitle}</title>
        <meta name="description" content={data?.data?.metaDescription} />
      </Head>

      <PageLayout
        stickyHeader={false}
        header={data?.data?.header?.data}
        footer={data?.data?.footer?.data}
        slugs={slugs}
        subHeader={(
          <ResidenceMenuNavigation
            name={residence?.data?.nameShort}
            menu={data?.data?.menuNavigationActivity}
            displayContact={data?.data?.displayContact}
            displayBooking={data?.data?.displayBooking}
            logo={data?.data?.logo}
            phone={data?.data?.phone}
            bookingUrl={bookingUrl}
            pmsId={residence?.data?.pmsId}
          />
        )}
      >
        <PageTransition>
          {data?.data?.blocks?.length ? data?.data?.blocks.map((block, index) => (
            <Fragment key={`${block.type}-${index}`}>
              {renderBlock(block, {
                residence: residence?.data,
                bookingUrl,
                activity: data?.data?.activity,
                activityFullyBooked: data?.data?.fullyBooked,
                phone: data?.data?.phone,
                displayBooking: data?.data?.displayBooking,
                displayContact: data?.data?.displayContact,
              })}
            </Fragment>
          )) : null}
        </PageTransition>

        <BreadCrumb items={breadcrumbItems} />
      </PageLayout>
    </>
  );
}

PageResidence.propTypes = {
  page: PropTypes.arrayOf(PropTypes.shape({
    data: PropTypes.shape({
      blocks: PropTypes.arrayOf(PropTypes.shape({
        type: PropTypes.string,
        data: PropTypes.shape({}),
      })),
    }),
  })).isRequired,
  slugs: PropTypes.shape({
    fr: PropTypes.string,
    en: PropTypes.string,
  }),
};

PageResidence.defaultProps = {
  slugs: {},
};

export default PageResidence;
